import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page-header';
import { useAuth } from '@/lib/auth';
import { api, ApiError } from '@/lib/utils';

type PreviewRow = {
  id: string;
  shortcode: string;
  targetName?: string | null;
  status: string;
  openedCount: number;
  url: string;
  approvedAt?: string | null;
  expiresAt: string;
  createdAt: string;
};

export function AdminPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [tab, setTab] = useState<
    'previews' | 'support' | 'suppressions' | 'audit' | 'health' | 'early' | 'social'
  >('previews');
  const [earlyAccess, setEarlyAccess] = useState<unknown[]>([]);
  const [socialAudits, setSocialAudits] = useState<Array<{ id: string; businessId: string; status: string }>>(
    [],
  );
  const [socialBusinessId, setSocialBusinessId] = useState('');
  const [socialFinding, setSocialFinding] = useState({
    auditId: '',
    platform: 'Google Business Profile',
    observation: '',
    recommendation: '',
    evidenceUrl: '',
  });
  const [previews, setPreviews] = useState<PreviewRow[]>([]);
  const [csv, setCsv] = useState(
    'business_name,website,email,city,trade\nAcme Plumbing,https://example.com,owner@example.com,Austin,Plumbing\n',
  );
  const [uploadMsg, setUploadMsg] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<{
    canPublish: boolean;
    publishBlockReason?: string | null;
    top3: Array<{ title?: string }>;
  } | null>(null);
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookupResult, setLookupResult] = useState<string>('');
  const [suppressDomain, setSuppressDomain] = useState('');
  const [suppressions, setSuppressions] = useState<unknown[]>([]);
  const [audit, setAudit] = useState<unknown[]>([]);
  const [health, setHealth] = useState<Record<string, unknown> | null>(null);
  const [queues, setQueues] = useState<Record<string, unknown> | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate({ to: '/login' });
      return;
    }
    api('/api/v1/admin/me')
      .then(() => setAllowed(true))
      .catch((err) => {
        if ((err as ApiError).status === 404) setAllowed(false);
        else setAllowed(false);
      });
  }, [user, authLoading, navigate]);

  const loadPreviews = async () => {
    const data = await api<{ previews: PreviewRow[] }>('/api/v1/admin/previews');
    setPreviews(data.previews);
  };

  useEffect(() => {
    if (!allowed) return;
    if (tab === 'previews') void loadPreviews().catch(() => undefined);
    if (tab === 'suppressions') {
      api<{ suppressions: unknown[] }>('/api/v1/admin/suppressions')
        .then((d) => setSuppressions(d.suppressions))
        .catch(() => undefined);
    }
    if (tab === 'audit') {
      api<{ entries: unknown[] }>('/api/v1/admin/audit')
        .then((d) => setAudit(d.entries))
        .catch(() => undefined);
    }
    if (tab === 'health') {
      Promise.all([
        api<Record<string, unknown>>('/api/v1/admin/health'),
        api<Record<string, unknown>>('/api/v1/admin/queues'),
      ]).then(([h, q]) => {
        setHealth(h);
        setQueues(q);
      });
    }
    if (tab === 'early') {
      api<{ requests: unknown[] }>('/api/v1/admin/early-access')
        .then((d) => setEarlyAccess(d.requests))
        .catch(() => undefined);
    }
    if (tab === 'social') {
      api<{ audits: Array<{ id: string; businessId: string; status: string }> }>(
        '/api/v1/admin/social-audits',
      )
        .then((d) => setSocialAudits(d.audits))
        .catch(() => undefined);
    }
  }, [allowed, tab]);

  if (authLoading || allowed === null) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="mx-auto max-w-md px-4 py-24 text-center">
        <h1 className="text-xl font-semibold">Not found</h1>
        <p className="mt-2 text-sm text-text-secondary">This page is not available.</p>
        <a href="/app" className="mt-6 inline-flex">
          <Button variant="secondary">Home</Button>
        </a>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 px-4 py-8">
      <PageHeader
        title="Admin"
        description="Staff only. Every view is audit-logged. Previews require human approval before publish."
      />

      <nav className="flex flex-wrap gap-2 border-b border-border pb-2">
        {(
          [
            ['previews', 'Preview generator'],
            ['support', 'Support lookup'],
            ['suppressions', 'Suppressions'],
            ['early', 'Early access'],
            ['social', 'Social audits'],
            ['audit', 'Audit log'],
            ['health', 'Health'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={
              tab === id
                ? 'rounded-lg bg-brand/10 px-3 py-1.5 text-sm font-medium text-brand'
                : 'rounded-lg px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-subtle'
            }
          >
            {label}
          </button>
        ))}
      </nav>

      {tab === 'previews' && (
        <div className="space-y-6">
          <div className="space-y-3 rounded-xl border border-border p-4">
            <h2 className="font-semibold">Upload CSV</h2>
            <p className="text-sm text-text-secondary">
              Columns: business_name, website, email, city, trade. Dedupes by host; skips suppressions.
            </p>
            <textarea
              className="min-h-[120px] w-full rounded-lg border border-border bg-bg-surface p-3 font-mono text-xs"
              value={csv}
              onChange={(e) => setCsv(e.target.value)}
            />
            <div className="flex flex-wrap gap-2">
              <Button
                loading={busy}
                onClick={async () => {
                  setBusy(true);
                  setUploadMsg('');
                  try {
                    const res = await api<{ created: unknown[]; skipped: unknown[] }>(
                      '/api/v1/admin/previews/csv',
                      { method: 'POST', body: JSON.stringify({ csv }) },
                    );
                    setUploadMsg(
                      `Queued ${res.created.length}; skipped ${res.skipped.length}. Approve before publish.`,
                    );
                    await loadPreviews();
                  } catch (err) {
                    setUploadMsg((err as ApiError).message);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Queue reviews
              </Button>
              <a href="/api/v1/admin/previews/export/merge">
                <Button variant="secondary">Download merge CSV</Button>
              </a>
            </div>
            {uploadMsg && <p className="text-sm text-text-secondary">{uploadMsg}</p>}
          </div>

          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-bg-muted text-text-secondary">
                <tr>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Opens</th>
                  <th className="px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {previews.map((p) => (
                  <tr key={p.id} className="border-b border-border">
                    <td className="px-3 py-2">
                      <div className="font-medium">{p.targetName || p.shortcode}</div>
                      <div className="text-xs text-text-muted">{p.shortcode}</div>
                    </td>
                    <td className="px-3 py-2">{p.status}</td>
                    <td className="px-3 py-2">{p.openedCount}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            setSelectedId(p.id);
                            const d = await api<{
                              canPublish: boolean;
                              publishBlockReason?: string | null;
                              top3: Array<{ title?: string }>;
                            }>(`/api/v1/admin/previews/${p.id}`);
                            setDetail(d);
                          }}
                        >
                          Inspect
                        </Button>
                        {p.status === 'awaiting_approval' && (
                          <Button
                            size="sm"
                            onClick={async () => {
                              try {
                                await api(`/api/v1/admin/previews/${p.id}/approve`, {
                                  method: 'POST',
                                });
                                await loadPreviews();
                              } catch (err) {
                                setUploadMsg((err as ApiError).message);
                              }
                            }}
                          >
                            Approve & publish
                          </Button>
                        )}
                        {p.status === 'published' && (
                          <a href={p.url} target="_blank" rel="noreferrer" className="text-brand text-xs underline">
                            Open /r
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {selectedId && detail && (
            <div className="rounded-xl border border-border p-4 text-sm">
              <h3 className="font-semibold">Preview {selectedId}</h3>
              <p className="mt-1 text-text-secondary">
                Can publish: {detail.canPublish ? 'yes' : 'no'}
                {detail.publishBlockReason ? ` — ${detail.publishBlockReason}` : ''}
              </p>
              <ul className="mt-2 list-disc pl-5">
                {detail.top3.map((t, i) => (
                  <li key={i}>{t.title}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {tab === 'support' && (
        <div className="space-y-4 rounded-xl border border-border p-4">
          <h2 className="font-semibold">Support lookup (read-only, logged)</h2>
          <Input
            label="User email"
            value={lookupEmail}
            onChange={(e) => setLookupEmail(e.target.value)}
          />
          <Button
            size="sm"
            onClick={async () => {
              try {
                const d = await api<{ user: unknown; business: unknown }>(
                  `/api/v1/admin/support/lookup?email=${encodeURIComponent(lookupEmail)}`,
                );
                setLookupResult(JSON.stringify(d, null, 2));
              } catch (err) {
                setLookupResult((err as ApiError).message);
              }
            }}
          >
            Look up
          </Button>
          {lookupResult && (
            <pre className="max-h-80 overflow-auto rounded-lg bg-bg-muted p-3 text-xs">{lookupResult}</pre>
          )}
        </div>
      )}

      {tab === 'suppressions' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-2">
            <Input
              label="Domain to suppress"
              value={suppressDomain}
              onChange={(e) => setSuppressDomain(e.target.value)}
            />
            <Button
              size="sm"
              onClick={async () => {
                await api('/api/v1/admin/suppressions', {
                  method: 'POST',
                  body: JSON.stringify({ domain: suppressDomain, reason: 'manual_admin' }),
                });
                const d = await api<{ suppressions: unknown[] }>('/api/v1/admin/suppressions');
                setSuppressions(d.suppressions);
                setSuppressDomain('');
              }}
            >
              Add
            </Button>
          </div>
          <pre className="max-h-96 overflow-auto rounded-xl border border-border p-3 text-xs">
            {JSON.stringify(suppressions, null, 2)}
          </pre>
        </div>
      )}

      {tab === 'audit' && (
        <pre className="max-h-[32rem] overflow-auto rounded-xl border border-border p-3 text-xs">
          {JSON.stringify(audit, null, 2)}
        </pre>
      )}

      {tab === 'health' && (
        <div className="space-y-4 text-sm">
          <pre className="rounded-xl border border-border p-3 text-xs">{JSON.stringify(health, null, 2)}</pre>
          <pre className="rounded-xl border border-border p-3 text-xs">{JSON.stringify(queues, null, 2)}</pre>
        </div>
      )}

      {tab === 'early' && (
        <pre className="max-h-[32rem] overflow-auto rounded-xl border border-border p-3 text-xs">
          {JSON.stringify(earlyAccess, null, 2)}
        </pre>
      )}

      {tab === 'social' && (
        <div className="space-y-6">
          <div className="space-y-3 rounded-xl border border-border p-4">
            <h2 className="font-semibold">Create audit (owner private workspace only)</h2>
            <Input
              label="Business ID"
              value={socialBusinessId}
              onChange={(e) => setSocialBusinessId(e.target.value)}
            />
            <Button
              size="sm"
              loading={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await api('/api/v1/admin/social-audits', {
                    method: 'POST',
                    body: JSON.stringify({ businessId: socialBusinessId }),
                  });
                  const d = await api<{ audits: typeof socialAudits }>('/api/v1/admin/social-audits');
                  setSocialAudits(d.audits);
                  setUploadMsg('Audit created');
                } catch (err) {
                  setUploadMsg((err as ApiError).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Create
            </Button>
          </div>
          <div className="space-y-3 rounded-xl border border-border p-4">
            <h2 className="font-semibold">Add finding</h2>
            <Input
              label="Audit ID"
              value={socialFinding.auditId}
              onChange={(e) => setSocialFinding((f) => ({ ...f, auditId: e.target.value }))}
            />
            <Input
              label="Platform"
              value={socialFinding.platform}
              onChange={(e) => setSocialFinding((f) => ({ ...f, platform: e.target.value }))}
            />
            <Input
              label="Observation"
              value={socialFinding.observation}
              onChange={(e) => setSocialFinding((f) => ({ ...f, observation: e.target.value }))}
            />
            <Input
              label="Recommendation"
              value={socialFinding.recommendation}
              onChange={(e) => setSocialFinding((f) => ({ ...f, recommendation: e.target.value }))}
            />
            <Input
              label="Evidence URL"
              value={socialFinding.evidenceUrl}
              onChange={(e) => setSocialFinding((f) => ({ ...f, evidenceUrl: e.target.value }))}
            />
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={async () => {
                  await api(`/api/v1/admin/social-audits/${socialFinding.auditId}/findings`, {
                    method: 'POST',
                    body: JSON.stringify({
                      platform: socialFinding.platform,
                      observation: socialFinding.observation,
                      recommendation: socialFinding.recommendation,
                      evidenceUrl: socialFinding.evidenceUrl || null,
                      confidence: 'manually_reviewed',
                    }),
                  });
                  setUploadMsg('Finding saved');
                }}
              >
                Save finding
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={async () => {
                  await api(`/api/v1/admin/social-audits/${socialFinding.auditId}/publish`, {
                    method: 'POST',
                  });
                  setUploadMsg('Published to owner private workspace');
                  const d = await api<{ audits: typeof socialAudits }>('/api/v1/admin/social-audits');
                  setSocialAudits(d.audits);
                }}
              >
                Publish to owner
              </Button>
            </div>
            {uploadMsg && <p className="text-sm text-text-secondary">{uploadMsg}</p>}
          </div>
          <pre className="max-h-96 overflow-auto rounded-xl border border-border p-3 text-xs">
            {JSON.stringify(socialAudits, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
